/**
 * trackStore: Redux 외부에서 JitsiTrack 객체를 관리하는 경량 스토어
 *
 * JitsiTrack은 non-serializable이므로 Redux에 넣지 않고 외부 스토어로 관리합니다.
 * useSyncExternalStore와 함께 사용하여 특정 참여자의 트랙이 변경될 때
 * 해당 참여자의 컴포넌트만 리렌더링되도록 합니다.
 */
import { useSyncExternalStore } from 'react';
import type { JitsiLocalTrack, JitsiRemoteTrack } from '@/types/jitsi';

// ─── 타입 ─────────────────────────────────────────────────────────────────────

export interface RemoteTrackSet {
  video: JitsiRemoteTrack | null;
  audio: JitsiRemoteTrack | null;
  screenShare: JitsiRemoteTrack | null;
}

export interface TrackState {
  localVideo: JitsiLocalTrack | null;
  localAudio: JitsiLocalTrack | null;
  localScreenShare: JitsiLocalTrack | null;
  remote: Record<string, RemoteTrackSet>;
}

type Listener = () => void;

const EMPTY_REMOTE: RemoteTrackSet = { video: null, audio: null, screenShare: null };

// ─── Store ────────────────────────────────────────────────────────────────────

class TrackStore {
  private _data: TrackState = {
    localVideo: null,
    localAudio: null,
    localScreenShare: null,
    remote: {},
  };

  // 전체 구독 리스너
  private _listeners = new Set<Listener>();
  // 로컬 비디오 구독 리스너
  private _localVideoListeners = new Set<Listener>();
  // 로컬 오디오 구독 리스너
  private _localAudioListeners = new Set<Listener>();
  // 로컬 화면공유 구독 리스너
  private _localScreenShareListeners = new Set<Listener>();
  // 참여자별 구독 리스너
  private _perUserListeners = new Map<string, Set<Listener>>();

  // ── 스냅샷 ──
  getSnapshot = (): TrackState => this._data;
  getLocalVideo = (): JitsiLocalTrack | null => this._data.localVideo;
  getLocalAudio = (): JitsiLocalTrack | null => this._data.localAudio;
  getLocalScreenShare = (): JitsiLocalTrack | null => this._data.localScreenShare;
  getRemote = (): Record<string, RemoteTrackSet> => this._data.remote;

  getRemoteUser(userId: string): RemoteTrackSet {
    return this._data.remote[userId] ?? EMPTY_REMOTE;
  }

  // ── 전체 구독 ──
  subscribe = (listener: Listener): (() => void) => {
    this._listeners.add(listener);
    return () => { this._listeners.delete(listener); };
  };

  // ── 로컬 트랙별 구독 ──
  subscribeLocalVideo = (listener: Listener): (() => void) => {
    this._localVideoListeners.add(listener);
    return () => { this._localVideoListeners.delete(listener); };
  };

  subscribeLocalAudio = (listener: Listener): (() => void) => {
    this._localAudioListeners.add(listener);
    return () => { this._localAudioListeners.delete(listener); };
  };

  subscribeLocalScreenShare = (listener: Listener): (() => void) => {
    this._localScreenShareListeners.add(listener);
    return () => { this._localScreenShareListeners.delete(listener); };
  };

  // ── 참여자별 구독 ──
  subscribeUser(userId: string, listener: Listener): () => void {
    if (!this._perUserListeners.has(userId)) {
      this._perUserListeners.set(userId, new Set());
    }
    this._perUserListeners.get(userId)!.add(listener);
    return () => { this._perUserListeners.get(userId)?.delete(listener); };
  }

  // ── 뮤테이션 ──
  setLocalVideoTrack(track: JitsiLocalTrack | null): void {
    if (this._data.localVideo === track) return;
    this._data = { ...this._data, localVideo: track };
    this._emit(this._localVideoListeners);
    this._emit(this._listeners);
  }

  setLocalAudioTrack(track: JitsiLocalTrack | null): void {
    if (this._data.localAudio === track) return;
    this._data = { ...this._data, localAudio: track };
    this._emit(this._localAudioListeners);
    this._emit(this._listeners);
  }

  setLocalScreenShareTrack(track: JitsiLocalTrack | null): void {
    if (this._data.localScreenShare === track) return;
    this._data = { ...this._data, localScreenShare: track };
    this._emit(this._localScreenShareListeners);
    this._emit(this._listeners);
  }

  setRemoteTrack(
    userId: string,
    kind: 'video' | 'audio' | 'screenShare',
    track: JitsiRemoteTrack | null,
  ): void {
    const prev = this._data.remote[userId] ?? EMPTY_REMOTE;
    if (prev[kind] === track) return;
    this._data = {
      ...this._data,
      remote: {
        ...this._data.remote,
        [userId]: { ...prev, [kind]: track },
      },
    };
    this._emitUser(userId);
    this._emit(this._listeners);
  }

  removeParticipantTracks(userId: string): void {
    if (!(userId in this._data.remote)) return;
    const { [userId]: _removed, ...rest } = this._data.remote;
    this._data = { ...this._data, remote: rest };
    this._emitUser(userId);
    this._emit(this._listeners);
    // 구독 리스너 정리
    this._perUserListeners.delete(userId);
  }

  // ── 내부 ──
  private _emit(listeners: Set<Listener>): void {
    listeners.forEach((fn) => fn());
  }

  private _emitUser(userId: string): void {
    this._perUserListeners.get(userId)?.forEach((fn) => fn());
  }
}

// 싱글턴 인스턴스
export const trackStore = new TrackStore();

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useLocalVideoTrack(): JitsiLocalTrack | null {
  return useSyncExternalStore(trackStore.subscribeLocalVideo, trackStore.getLocalVideo);
}

export function useLocalAudioTrack(): JitsiLocalTrack | null {
  return useSyncExternalStore(trackStore.subscribeLocalAudio, trackStore.getLocalAudio);
}

export function useLocalScreenShareTrack(): JitsiLocalTrack | null {
  return useSyncExternalStore(trackStore.subscribeLocalScreenShare, trackStore.getLocalScreenShare);
}

export function useRemoteTracks(userId: string): RemoteTrackSet {
  return useSyncExternalStore(
    (cb) => trackStore.subscribeUser(userId, cb),
    () => trackStore.getRemoteUser(userId),
  );
}

export function useAllRemoteTracks(): Record<string, RemoteTrackSet> {
  return useSyncExternalStore(trackStore.subscribe, trackStore.getRemote);
}
